import { FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FormField, FormLabel, FormSelect, FormInput, FormCheckbox } from '../ui/Form';
import { Button } from '../ui';
import { emailService } from '../../services';
import type { EmailSearchFilters, EmailCredential } from '../../types/email';

interface EmailFiltersProps {
  filters: EmailSearchFilters;
  onFilterChange: (filters: EmailSearchFilters) => void;
  credentials?: EmailCredential[];
  showCredentialFilter?: boolean;
}

function EmailFilters({
  filters,
  onFilterChange,
  credentials = [],
  showCredentialFilter = false,
}: EmailFiltersProps) {
  // Fetch folders
  const { data: folders } = useQuery({
    queryKey: ['email-folders'],
    queryFn: () => emailService.getFolders(),
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const newFilters: EmailSearchFilters = {
      folder_id: Number(formData.get('folder_id')) || undefined,
      email_credential_id: Number(formData.get('email_credential_id')) || undefined,
      sender_email: (formData.get('sender_email') as string) || undefined,
      subject: (formData.get('subject') as string) || undefined,
      category: (formData.get('category') as string) || undefined,
      white_black_list: Number(formData.get('white_black_list')) || undefined,
      sort_direction: (formData.get('sort_direction') as 'asc' | 'desc') || 'desc',
      page: 1, // Reset to first page when filters change
    };

    // Only add checkbox values if they are checked
    if (formData.get('classified_by_ai') === 'on') {
      newFilters.classified_by_ai = true;
    }

    if (formData.get('has_attachments') === 'on') {
      newFilters.has_attachments = true;
    }

    if (formData.get('is_read') === 'on') {
      newFilters.is_read = true;
    }

    if (formData.get('not_read') === 'on') {
      newFilters.is_read = false;
    }

    if (formData.get('is_starred') === 'on') {
      newFilters.is_starred = true;
    }

    // Remove undefined values
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key as keyof EmailSearchFilters] === undefined) {
        delete newFilters[key as keyof EmailSearchFilters];
      }
    });

    onFilterChange(newFilters);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-lg p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <FormField>
          <FormLabel htmlFor="folder_id">Pasta</FormLabel>
          <FormSelect id="folder_id" name="folder_id" defaultValue={filters.folder_id?.toString()}>
            <option value="">Todas as pastas</option>
            {folders?.map(folder => (
              <option key={folder.id} value={folder.id}>
                {folder.name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        {showCredentialFilter && (
          <FormField>
            <FormLabel htmlFor="email_credential_id">Conta de Email</FormLabel>
            <FormSelect
              id="email_credential_id"
              name="email_credential_id"
              defaultValue={filters.email_credential_id?.toString()}
            >
              <option value="">Todas as contas</option>
              {credentials.map(credential => (
                <option key={credential.id} value={credential.id}>
                  {credential.name}
                </option>
              ))}
            </FormSelect>
          </FormField>
        )}

        <FormField>
          <FormLabel htmlFor="sender_email">Remetente</FormLabel>
          <FormInput
            id="sender_email"
            name="sender_email"
            defaultValue={filters.sender_email}
            placeholder="Email do remetente"
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="subject">Assunto</FormLabel>
          <FormInput
            id="subject"
            name="subject"
            defaultValue={filters.subject}
            placeholder="Assunto do email"
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="category">Categoria</FormLabel>
          <FormInput
            id="category"
            name="category"
            defaultValue={filters.category}
            placeholder="Categoria"
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="white_black_list">Lista</FormLabel>
          <FormSelect
            id="white_black_list"
            name="white_black_list"
            defaultValue={filters.white_black_list?.toString()}
          >
            <option value="">Todas</option>
            <option value="1">Whitelist</option>
            <option value="2">Blacklist</option>
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="sort_direction">Ordenação</FormLabel>
          <FormSelect
            id="sort_direction"
            name="sort_direction"
            defaultValue={filters.sort_direction || 'desc'}
          >
            <option value="desc">Mais recentes primeiro</option>
            <option value="asc">Mais antigos primeiro</option>
          </FormSelect>
        </FormField>
      </div>

      <div className="flex flex-wrap gap-6 mt-4">
        <FormField>
          <div className="flex items-center">
            <FormCheckbox
              id="classified_by_ai"
              name="classified_by_ai"
              defaultChecked={filters.classified_by_ai}
            />
            <FormLabel htmlFor="classified_by_ai" className="ml-2 mb-0">
              Classificado por IA
            </FormLabel>
          </div>
        </FormField>

        <FormField>
          <div className="flex items-center">
            <FormCheckbox
              id="has_attachments"
              name="has_attachments"
              defaultChecked={filters.has_attachments}
            />
            <FormLabel htmlFor="has_attachments" className="ml-2 mb-0">
              Com anexos
            </FormLabel>
          </div>
        </FormField>

        <FormField>
          <div className="flex items-center">
            <FormCheckbox id="is_read" name="is_read" defaultChecked={filters.is_read === true} />
            <FormLabel htmlFor="is_read" className="ml-2 mb-0">
              Lidos
            </FormLabel>
          </div>
        </FormField>

        <FormField>
          <div className="flex items-center">
            <FormCheckbox
              id="not_read"
              name="not_read"
              defaultChecked={filters.is_read === false}
            />
            <FormLabel htmlFor="not_read" className="ml-2 mb-0">
              Não lidos
            </FormLabel>
          </div>
        </FormField>

        <FormField>
          <div className="flex items-center">
            <FormCheckbox id="is_starred" name="is_starred" defaultChecked={filters.is_starred} />
            <FormLabel htmlFor="is_starred" className="ml-2 mb-0">
              Com estrela
            </FormLabel>
          </div>
        </FormField>
      </div>

      <div className="flex justify-end mt-4">
        <Button type="submit">Filtrar</Button>
      </div>
    </form>
  );
}

export default EmailFilters;
